import React, { Suspense, useEffect, useState } from 'react';
import { Facebook as LoadingState } from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Tag from 'components/Tag';
import Icon from 'components/icons/Icon';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import GeneralDataBox from 'components/boxes/GeneralDataBox';
import EmptyStateBox from 'components/boxes/EmptyStateBox/EmptyStateBox';
import {
  SectionHeader,
  EngagementGrid,
  EngagementCard,
  AveragesSection,
  AveragesGrid,
  PostsSection,
  PostsGrid,
  PostCard
} from './Styled';

import { selectUser } from 'redux/slices/userSlice';
import {
  selectPerformance,
  setPerformance
} from 'redux/slices/PerformanceSection/PerformanceSectionSlice';
import { getAmbassadorPerformance } from 'services/rest/PerformanceSection/PerformanceSection';
import { useFetch } from 'hooks/useFetch.hook';
import { getInfoUser } from 'services/rest/User/getInfoUser';
import { nFormatter } from 'helpers/formatHelper';

const FacebookSection = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['facebook_section', 'performance_section']);
  const { currentUser } = useSelector(selectUser);
  const { performanceSection } = useSelector(selectPerformance);
  const [localLoading, setLocalLoading] = useState(true);
  const [posts, setPosts] = useState<any[]>([]);
  const { data } = useFetch(
    getInfoUser(currentUser?.user?.id, currentUser?.token, currentUser?.role)
  );
  const userData = data as any;

  const getData = async () => {
    try {
      const { data } = await getAmbassadorPerformance(currentUser?.token);
      dispatch(setPerformance(data.data));
      const album = data?.data?.dataFacebook?.albumFacebook;
      if (album && Array.isArray(album)) {
        setPosts(album);
      }
    } catch {
      // Silently handle
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  if (localLoading || !userData) {
    return <LoadingState height="230px" width="100%" />;
  }

  if (!userData?.linkedFacebook) {
    return (
      <SectionWrapper>
        <SectionHeader>
          <Tag network="facebook" variant="network" />
          <h1>{t('facebook_section:title', 'My Facebook Activity')}</h1>
        </SectionHeader>
        <EmptyStateBox
          title={t(
            'facebook_section:connect_account',
            'Connect your Facebook account to see your activity'
          )}
          description={t(
            'facebook_section:connect_account_description',
            'Link your Facebook account to track your posts, reach and points earned'
          )}
        />
      </SectionWrapper>
    );
  }

  const fbData = performanceSection?.dataFacebook;

  const summaryItems = [
    {
      icon: <Icon name="report" size="big" />,
      value: performanceSection?.postsByNetwork?.facebook
        ? performanceSection.postsByNetwork.facebook.toString()
        : '0',
      text: t('facebook_section:total_posts', 'Posts published')
    },
    {
      icon: <Icon name="total-reach" size="big" />,
      value: nFormatter(performanceSection?.potentialReach),
      text: t('facebook_section:potential_reach', 'Potential reach')
    },
    {
      icon: <Icon name="star" size="big" />,
      value: performanceSection?.annualRanking?.totalPoints
        ? performanceSection.annualRanking.totalPoints.toString()
        : '0',
      text: t('facebook_section:points_earned', 'Points earned')
    }
  ];

  return (
    <Suspense fallback={<LoadingState height="230px" width="100%" />}>
      <SectionWrapper>
        <SectionHeader>
          <Tag network="facebook" variant="network" />
          <h1>{t('facebook_section:title', 'My Facebook Activity')}</h1>
        </SectionHeader>

        <GeneralDataBox items={summaryItems} />

        <EngagementGrid>
          <EngagementCard>
            <p className="value">{nFormatter(fbData?.totalLikes)}</p>
            <p className="label">
              {t('facebook_section:total_likes', 'Total Likes')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(fbData?.totalComments)}</p>
            <p className="label">
              {t('facebook_section:total_comments', 'Total Comments')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(fbData?.totalShares)}</p>
            <p className="label">
              {t('facebook_section:total_shares', 'Total Shares')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{fbData?.postsProcessed?.toString() || '0'}</p>
            <p className="label">
              {t('facebook_section:posts_processed', 'Posts Analyzed')}
            </p>
          </EngagementCard>
        </EngagementGrid>

        <AveragesSection>
          <h2>{t('facebook_section:averages_title', 'Average per Post')}</h2>
          <AveragesGrid>
            <EngagementCard>
              <p className="value">{fbData?.averageLikes || '0'}</p>
              <p className="label">
                {t('facebook_section:avg_likes', 'Avg. Likes')}
              </p>
            </EngagementCard>
            <EngagementCard>
              <p className="value">{fbData?.averageComments || '0'}</p>
              <p className="label">
                {t('facebook_section:avg_comments', 'Avg. Comments')}
              </p>
            </EngagementCard>
            <EngagementCard>
              <p className="value">{fbData?.averageShares || '0'}</p>
              <p className="label">
                {t('facebook_section:avg_shares', 'Avg. Shares')}
              </p>
            </EngagementCard>
          </AveragesGrid>
        </AveragesSection>

        <PostsSection>
          <h2>{t('facebook_section:recent_posts', 'Recent Posts')}</h2>
          {posts.length > 0 ? (
            <PostsGrid>
              {posts.map((post: any, index: number) => (
                <PostCard
                  key={index}
                  href={post.permalinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {post.url ? (
                    <img
                      className="post-image"
                      src={post.url}
                      alt={post.message || 'Facebook post'}
                    />
                  ) : (
                    <div className="post-placeholder">f</div>
                  )}
                  <div className="post-body">
                    {post.message && (
                      <p className="post-message">{post.message}</p>
                    )}
                    <p className="post-date">
                      {new Date(post.createdTime).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="post-metrics">
                    <div className="post-metric">
                      <span>{post.postLikes}</span> likes
                    </div>
                    <div className="post-metric">
                      <span>{post.postComments}</span> comments
                    </div>
                    <div className="post-metric">
                      <span>{post.postShares}</span> shares
                    </div>
                  </div>
                </PostCard>
              ))}
            </PostsGrid>
          ) : (
            <EmptyStateBox
              title={t('facebook_section:no_posts', 'No posts available yet')}
              description={t(
                'facebook_section:no_posts_description',
                'Your recent Facebook posts will appear here'
              )}
            />
          )}
        </PostsSection>
      </SectionWrapper>
    </Suspense>
  );
};

export default FacebookSection;
